import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomersService, CustomerPagedResult } from '../../../services/customers.service';
import { StatusService } from '../../../services/status.service';
import { Customer } from '../../../models/Customer';
import { Status } from '../../../models/Status';
import { TableAction } from '../../../shared/components/table/table.component';
import { CUSTOMER_CATEGORY_FILTER_OPTIONS } from '../../../models/CustomerEnums';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.css']
})
export class CustomersComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  isLoading = false;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalCustomers = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  filters = {
    status: '', // 'Ativo' | 'Inativo'
    blocked: '', // 'Bloqueado' | 'Não Bloqueado'
    category: '', // categoria do cliente
    searchTerm: '' // busca por termo
  };

  sortBy: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Debounce para busca
  private searchTimeout: any = null;

  statusOptions: any[] = [];

  blockedOptions = [
    { value: '', label: 'Todos' },
    { value: 'Bloqueado', label: 'Bloqueado' },
    { value: 'NaoBloqueado', label: 'Não Bloqueado' }
  ];

  categoryOptions = CUSTOMER_CATEGORY_FILTER_OPTIONS;

  // Status dinâmicos
  loadingStatuses = false;

  // Configuração da tabela
  tableTitle = 'Clientes';
  showSearch = true;
  searchPlaceholder = 'Buscar por nome, documento, email...';
  clickable = true;

  columns = [
    { titulo: 'Razão Social', label: 'name' },
    { 
      titulo: 'Documento', 
      label: 'documentNumber',
      formatter: (value: string) => this.formatDocument(value)
    },
    { titulo: 'Categoria', label: 'customerCategory' },
    { titulo: 'Email', label: 'email' },
    { 
      titulo: 'Telefone', 
      label: 'primaryPhone',
      formatter: (value: string) => this.formatPhone(value)
    }
  ];
  
  // Ações da tabela (clique na linha abre Visualizar)
  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  private isConfirmingDelete = false;

  // Exportação Excel (igual staff)
  showExportModal = false;
  isExporting = false;
  exportError: string | null = null;
  availableExportColumns: { key: string; label: string; checked: boolean }[] = [];
  loadingExportColumns = false;
  exportFilters = {
    status: null as string | null,
    category: null as string | null,
    searchTerm: null as string | null
  };
  private defaultExportKeys = ['Name', 'Email', 'DocumentNumber', 'Status', 'CustomerCategory'];

  constructor(
    private router: Router,
    private customersService: CustomersService,
    private statusService: StatusService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCustomerStatuses();
    this.loadCustomers();
  }

  ngOnDestroy(): void {
    // Limpa o timeout quando o componente for destruído
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    // Cancela todas as subscriptions ativas
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCustomers(): void {
    this.isLoading = true;
    const options: any = { 
      page: this.currentPage, 
      pageSize: this.pageSize 
    };
    
    // Adiciona filtros se existirem
    if (this.filters.status) {
      options.status = this.filters.status;
    }
    if (this.filters.category) {
      options.category = this.filters.category;
    }
    if (this.filters.searchTerm) {
      options.searchTerm = this.filters.searchTerm;
    }
    if (this.sortBy) {
      options.sortBy = this.sortBy;
      options.sortDirection = this.sortDirection;
    }
    
    this.customersService.getAllPaged(options).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: CustomerPagedResult) => {
        
        // Se a API não retorna paginação (totalRecords < 0), fazer paginação client-side
        if (result.totalRecords < 0) {
          this.customers = result.data;
          // Calcular paginação local
          const startIndex = (this.currentPage - 1) * this.pageSize;
          const endIndex = startIndex + this.pageSize;
          this.filteredCustomers = result.data.slice(startIndex, endIndex);
          this.totalCustomers = result.data.length;
          this.totalPages = Math.ceil(result.data.length / this.pageSize);
          this.hasNextPage = this.currentPage < this.totalPages;
          this.hasPreviousPage = this.currentPage > 1;
        } else {
          // API está paginando corretamente (nova referência para a tabela atualizar)
          this.customers = [...result.data];
          this.filteredCustomers = [...result.data];
          this.totalCustomers = result.totalRecords >= 0 ? result.totalRecords : 0;
          this.totalPages = result.totalPages >= 0 ? result.totalPages : -1;
          this.hasNextPage = result.hasNextPage;
          this.hasPreviousPage = result.hasPreviousPage;
          this.currentPage = result.page;
        }
        
        
        // Aplica filtros locais (blocked) se necessário
        this.applyFilters();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: _ => { this.isLoading = false; }
    });
  }

  private loadCustomerStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Customer', true).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (statuses: Status[]) => {
        // Adiciona opção "Todos" no início
        this.statusOptions = [
          { value: '', label: 'Todos' },
          ...statuses.map(status => ({
            value: status.name,
            label: status.name
          }))
        ];
        this.loadingStatuses = false;
      },
      error: (err) => {
        console.error('Erro ao carregar status:', err);
        this.loadingStatuses = false;
      }
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCustomers();
  }

  onPageSizeChange(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.currentPage = 1; // Reset para primeira página ao mudar o tamanho
    this.loadCustomers();
  }

  // Aplica filtros (status, category e searchTerm são aplicados no backend)
  applyFilters(): void {
    // Se já aplicou paginação client-side, não sobrescrever filteredCustomers
    // Apenas aplicar filtro de "blocked" se necessário na paginação client-side
    if (!this.filters.blocked) {
      // Se não tem filtro de blocked, não fazer nada (já foi paginado)
      return;
    }

    // Se tem filtro de blocked, aplicar sobre os dados já paginados
    let filtered = [...this.filteredCustomers];

    // Bloqueio (filtro local)
    if (this.filters.blocked === 'Bloqueado') {
      filtered = filtered.filter(c => c.blocked === true);
    } else if (this.filters.blocked === 'NaoBloqueado') {
      filtered = filtered.filter(c => c.blocked === false);
    }

    this.filteredCustomers = filtered;
  }
  
  onFiltersChange(): void {
    // Recarrega dados do backend quando filtros mudam
    this.currentPage = 1;
    this.loadCustomers();
  }

  onSearchChange(searchTerm: string): void {
    // Limpa o timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Atualiza o termo de busca imediatamente (para exibição)
    this.filters.searchTerm = searchTerm;

    // Aguarda 500ms após parar de digitar para fazer a chamada à API
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1;
      this.loadCustomers();
      this.searchTimeout = null;
    }, 500);
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    const apiSortMap: Record<string, string> = {
      name: 'Name',
      email: 'Email',
      customerCategory: 'CustomerCategory'
    };
    this.sortBy = apiSortMap[event.sortBy] ?? event.sortBy;
    this.sortDirection = event.sortOrder;
    this.currentPage = 1;
    this.loadCustomers();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.status || this.filters.category || this.filters.searchTerm || this.filters.blocked);
  }

  clearFilters(): void {
    this.filters = { status: '', blocked: '', category: '', searchTerm: '' };
    this.currentPage = 1;
    this.loadCustomers();
  }

  view(customer: Customer): void {
    this.router.navigate(['/commercial/customers', customer.id, 'view']);
  }

  edit(customer: Customer): void {
    this.router.navigate(['/commercial/customers', customer.id, 'edit']);
  }

  newCustomer(): void {
    this.router.navigate(['/commercial/customers/new']);
  }

  remove(id: number): void {
    if (this.isConfirmingDelete) return;
    this.isConfirmingDelete = true;

    const customer = this.customers.find(c => c.id === id);
    const name = customer?.name || 'este cliente';

    this.confirmationService.confirm({
      title: 'Excluir cliente',
      message: `Tem certeza que deseja excluir o cliente "${name}"?`,
      type: 'danger',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-secondary'
    }).subscribe(result => {
      this.isConfirmingDelete = false;
      if (!result) return;

      this.customersService.delete(id).subscribe({
        next: () => this.loadCustomers(),
        error: () => {}
      });
    });
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    const customer = item as Customer;

    switch (action) {
      case 'view':
        this.view(customer);
        break;
      case 'edit':
        this.edit(customer);
        break;
      case 'delete':
        this.remove(customer.id);
        break;
      default:
    }
  }

  // Formatação de documento (CPF/CNPJ)
  formatDocument(value: string): string {
    if (!value) return '-';
    
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 11) {
      // CPF: 000.000.000-00
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (digits.length === 14) {
      // CNPJ: 00.000.000/0000-00
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return value;
  }

  // Formatação de telefone
  formatPhone(value: string): string {
    if (!value) return '-';
    
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 11) {
      // Celular: (00) 00000-0000
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (digits.length === 10) {
      // Fixo: (00) 0000-0000
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    
    return value;
  }

  // ===== Exportação Excel =====

  openExportModal(): void {
    this.showExportModal = true;
    this.exportError = null;
    this.exportFilters = {
      status: this.filters.status || null,
      category: this.filters.category || null,
      searchTerm: this.filters.searchTerm || null
    };
    this.loadingExportColumns = true;
    this.customersService.getExportColumns().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (cols) => {
        this.availableExportColumns = cols.map(c => ({
          key: c.key,
          label: c.label,
          checked: this.defaultExportKeys.includes(c.key)
        }));
        this.loadingExportColumns = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingExportColumns = false;
        this.cdr.detectChanges();
      }
    });
  }

  closeExportModal(): void {
    this.showExportModal = false;
    this.exportError = null;
  }

  toggleExportField(key: string): void {
    const field = this.availableExportColumns.find(f => f.key === key);
    if (field) field.checked = !field.checked;
  }

  selectAllExportFields(): void {
    this.availableExportColumns.forEach(f => f.checked = true);
  }

  deselectAllExportFields(): void {
    this.availableExportColumns.forEach(f => f.checked = false);
  }

  generateExport(): void {
    const columns = this.availableExportColumns
      .filter(f => f.checked)
      .map(f => f.key);
    if (columns.length === 0) {
      this.exportError = 'Selecione pelo menos um campo para exportar';
      return;
    }
    this.isExporting = true;
    this.exportError = null;
    const body = {
      columns,
      status: this.exportFilters.status || null,
      category: this.exportFilters.category || null,
      searchTerm: this.exportFilters.searchTerm || null
    };
    this.customersService.exportToExcel(body).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        this.downloadExcelFile(blob);
        this.isExporting = false;
        this.closeExportModal();
      },
      error: () => {
        this.isExporting = false;
        this.exportError = 'Erro ao gerar planilha Excel';
      }
    });
  }

  private downloadExcelFile(blob: Blob): void {
    try {
      let excelBlob = blob;
      if (!blob.type || !blob.type.includes('spreadsheet')) {
        excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clientes_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      this.errorService.showSuccessMessage('Planilha Excel gerada e baixada com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer download do arquivo Excel:', error);
      this.exportError = 'Erro ao fazer download do arquivo Excel';
    }
  }
}

